import { useEffect, useReducer, useState } from 'react';
import {
  Action,
  initSections,
  reducer,
} from '../../../survey_engine/SurveyTemplates/dispatcher';
import { SectionData } from '../../../survey_engine/SurveyTemplates/Section';
import React from 'react';
import { PageHeader } from '../../../survey_engine/SurveyTemplates/PageHeader';
import { Card } from '../../../../components/Card';
import { CardHeader } from '../../../survey_engine/SurveyTemplates/CardHeader';
import { SectionList } from '../../../survey_engine/SurveyTemplates/SectionList';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { ScorecardTemplateService } from '../../../../services/v1/applicant_tracking/ScorecardTemplateService';
import { FIELD_CONFIGS, fetchTemplate, areSectionsSavable } from '../data';
import { NameInput } from '../../../survey_engine/SurveyTemplates/NameInput';
import { getRedirectUriQueryParam } from '../../../../utils/url';
import { InterviewRatingAlert } from '../InterviewRatingAlert';

const REDIRECT_URI =
  getRedirectUriQueryParam() ?? '/organization_settings/scorecard_templates';

interface PropTypes {
  id: number;
}

export default function EditScorecardTemplatePage(props: PropTypes) {
  const [name, setName] = useState<string>('');
  const [sections, dispatch] = useReducer(reducer, null, initSections);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    async function load() {
      const template = await fetchTemplate(props.id.toString());
      setName(template.name);
      dispatch({ type: 'reset', newState: template.sections });
      setIsLoading(false);
    }

    load();
  }, [props.id]);

  function isSavable() {
    if (!name.trim()) return false;
    if (!areSectionsSavable(sections)) return false;

    return true;
  }

  async function handleSave() {
    setIsLoading(true);

    await ScorecardTemplateService.update(props.id, {
      name,
      sections: sections.map((section) => ({
        id: section.sId,
        name: section.name,
        questions: section.fields.map((field) => ({
          id: field.serverId,
          title: field.title,
          fieldType: field.fieldType,
          max: field.max,
          aiFillEnabled: field.aiFillEnabled,
        })),
      })),
    });

    window.location.href = REDIRECT_URI;
  }

  return (
    <>
      {isLoading && <LoadingSpinner showBackdrop />}
      <UI
        isSavable={isSavable()}
        name={name}
        onName={setName}
        sections={sections}
        dispatch={dispatch}
        onSave={handleSave}
      />
    </>
  );
}

function UI(props: {
  isSavable: boolean;
  name: string;
  onName: (value: string) => void;
  sections: SectionData[];
  dispatch: React.Dispatch<Action>;
  onSave: () => void;
}) {
  return (
    <div className='container'>
      <PageHeader
        title={props.name}
        backUrl='/organization_settings/scorecard_templates'
      />
      <Card>
        <CardHeader
          title='Scorecard'
          isSavable={props.isSavable}
          onSaveClick={props.onSave}
          cancelUrl={REDIRECT_URI}
        />
        <div className='border-bottom'>
          <div className='d-flex flex-column w-50 fs-5 pt-2 pb-4 gap-4'>
            <NameInput
              name={props.name}
              placeholder='Insert Scorecard Name'
              onName={props.onName}
            />
          </div>
        </div>
        <SectionList
          fieldConfigs={FIELD_CONFIGS}
          singleTypeSections={true}
          sections={props.sections}
          dispatch={props.dispatch}
          title='Sections'
          subtitle='Define and manage your scorecard sections'
          columns={[
            { fieldName: 'type', className: 'col-3' },
            { fieldName: 'text', className: 'col' },
          ]}
        />
        <InterviewRatingAlert />
      </Card>
    </div>
  );
}
